import { DataLayerService } from '@monorepo-channels/shared/util-helpers'
import { CommonModule, Location } from '@angular/common'
import { Component, computed, inject, OnInit, Signal, signal } from '@angular/core'
import { MatIcon } from '@angular/material/icon'
import {
	getSelectedVenue,
	getStatusAvailabilities,
	getStatusConfirmReservation,
	getErrorConfirmation,
	getSelectedSection,
	getSelectedDate,
	isLoadingConfirmReservation,
	getAvailabilities,
	getAvailabilitiesDates,
	getSections,
	getSchedules,
	getMinMaxPartySize,
	getSelectedPartySize,
	getReservationTimes,
	getSelectedReservationTime,
	getConfirmReservation,
	getClient,
	ReservationActions,
	VenueActions,
	ClientActions,
	getVoucherValueReservation,
} from '@monorepo-channels/channels/domain'
import { Store } from '@ngrx/store'
import { combineLatest, map, Observable, take, tap } from 'rxjs'
import { MatTabsModule } from '@angular/material/tabs'
import { trigger, transition, style, animate } from '@angular/animations'
import { ActivatedRoute, Router, RouterOutlet, RouterModule } from '@angular/router'
import { MatButtonModule } from '@angular/material/button'
import { MatRadioModule } from '@angular/material/radio'
import { FormBuilder, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms'
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop'
import { BodyClassObserverService, isMobile } from '@monorepo-channels/shared/util-helpers'
import { Actions, ofType } from '@ngrx/effects'
import { GenericErrorComponent } from '../simple-bottom-sheets/generic-error.component'
import { MatProgressBarModule } from '@angular/material/progress-bar'
import { MatBottomSheet } from '@angular/material/bottom-sheet'
import {
	CalendarUiComponent,
	ConfirmSliderUiComponent,
	LoadingPageComponent,
	OrderSuccessComponent,
} from '@monorepo-channels/components/ui-porto-bank'
import { MatDialog } from '@angular/material/dialog'
import { MatFormFieldModule } from '@angular/material/form-field'
import { MatInputModule } from '@angular/material/input'
import { MatOptionModule } from '@angular/material/core'
import { MatSelectModule } from '@angular/material/select'
type ExtractObservableType<T> = T extends Observable<infer U> ? U : never
interface FieldState {
	focused: Signal<boolean>
	editing: Signal<boolean>
	setFocused: (value: boolean) => void
	setEditing: (value: boolean) => void
}

@Component({
	standalone: true,
	imports: [
		CommonModule,
		MatIcon,
		MatButtonModule,
		MatTabsModule,
		LoadingPageComponent,
		MatRadioModule,
		FormsModule,
		OrderSuccessComponent,
		RouterOutlet,
		MatProgressBarModule,
		ConfirmSliderUiComponent,
		RouterModule,
		MatFormFieldModule,
		MatInputModule,
		MatOptionModule,
		CalendarUiComponent,
		ReactiveFormsModule,
		MatSelectModule,
	],
	selector: 'feature-porto-bank-reservation',
	templateUrl: './reservation.component.html',
	styleUrl: './reservation.component.scss',
	animations: [
		trigger('slideIn', [
			transition(':enter', [
				style({ transform: 'translateX(100%)', opacity: 0 }),
				animate('300ms ease-out', style({ transform: 'translateX(0)', opacity: 1 })),
			]),
			transition(':leave', [
				animate('300ms ease-in', style({ transform: 'translateX(-100%)', opacity: 0 })),
			]),
		]),
	],
})
export class ReservationComponent implements OnInit {
	private route = inject(ActivatedRoute)
	private fb = inject(FormBuilder)
	private router = inject(Router)
	private store = inject(Store)
	private dialog = inject(MatDialog)
	isMobile = isMobile()
	public activeTab = signal(1)
	private actions$ = inject(Actions)
	public location = inject(Location)
	public selectedTab = signal('Data')
	public partysize = signal(1)
	public eula = signal(false)
	public eulaFromClient = signal(false)
	public editMode = signal(false)
	private venueId: string | null = null
	private bottomSheet = inject(MatBottomSheet)
	public principal$ = inject(BodyClassObserverService).observeClass('principal')
	public registrationFormValid = signal(false)
	public showTerms = signal(false)
	public showCalendar = signal(false)
	displayDate = ''
	private dataLayerService = inject(DataLayerService)

	fieldStates = new Map<string, FieldState>()
	form = this.fb.nonNullable.group({
		name: [{ value: '', disabled: true }],
		date: [{ value: '', disabled: false }, [Validators.required]],
		section: [{ value: '', disabled: false }, [Validators.required]],
		partySize: [{ value: null, disabled: false }, [Validators.required]],
		reservationTime: [{ value: '', disabled: false }, [Validators.required]],
	})
	formValid = toSignal(this.form.statusChanges.pipe(map(() => this.form.valid)), {
		initialValue: this.form.valid,
	})
	public isDisabled = computed(() => !this.eula() || !this.formValid())

	public vm$ = combineLatest([
		this.store.select(getSelectedVenue),
		this.store.select(getStatusAvailabilities),
		this.store.select(getStatusConfirmReservation),
		this.store.select(getErrorConfirmation),
		this.store.select(getSelectedSection),
		this.store.select(getSelectedDate),
		this.store.select(isLoadingConfirmReservation),
		this.store.select(getAvailabilities),
		this.store.select(getAvailabilitiesDates),
		this.store.select(getSections),
		this.store.select(getSchedules),
		this.store.select(getMinMaxPartySize),
		this.store.select(getSelectedPartySize),
		this.store.select(getReservationTimes),
		this.store.select(getSelectedReservationTime),
		this.store.select(getConfirmReservation),
		this.store.select(getClient),
		this.store.select(getVoucherValueReservation),
	]).pipe(
		tap(([, , , , , , , , , , , maxMinPartysize, selectedPartySize]) => {
			if (!selectedPartySize) {
				const min = maxMinPartysize?.min ?? 1
				this.partysize.set(min)
				this.setPartysize(min)
			}
		}),
		map(
			([
				venue,
				statusAvailabilities,
				statusConfirmReservation,
				errorReservation,
				selectedSection,
				selectedDate,
				isLoadingConfirmReservation,
				availabilities,
				availabilitiesDates,
				sections,
				schedules,
				maxMinPartysize,
				selectedPartySize,
				reservationTimes,
				selectedReservationTime,
				confirmReservation,
				client,
				voucherValue,
			]) => ({
				venue,
				statusAvailabilities,
				statusConfirmReservation,
				errorReservation,
				selectedSection,
				selectedDate,
				isLoadingConfirmReservation,
				availabilities,
				availabilitiesDates,
				sections,
				schedules,
				maxMinPartysize,
				selectedPartySize,
				reservationTimes,
				selectedReservationTime,
				confirmReservation,
				client,
				voucherValue,
			})
		)
	)

	constructor() {
		;['name', 'date', 'section', 'partySize', 'reservationTime'].forEach(fieldName =>
			this.initializeFieldState(fieldName)
		)
		this.store
			.select(getClient)
			.pipe(takeUntilDestroyed(), take(1))
			.subscribe(client => {
				const hasAcceptedEula = client?.agreements?.eula ?? false
				this.showTerms.set(!hasAcceptedEula)

				this.eulaFromClient.set(client?.agreements.eula ?? false)
				this.eula.set(client?.agreements.eula ?? false)
			})

		this.actions$
			.pipe(ofType(ReservationActions.loadAvailabilitiesFail), takeUntilDestroyed())
			.subscribe({
				next: error => {
					this.bottomSheet.open(GenericErrorComponent, {
						data: {
							error: error.error,
							title: 'Erro ao carregar as disponibilidades',
							btnMessage: 'Fechar',
						},
					})
				},
			})
		this.actions$
			.pipe(ofType(ReservationActions.confirmReservationFail), takeUntilDestroyed())
			.subscribe({
				next: error => {
					this.bottomSheet.open(GenericErrorComponent, {
						data: {
							error: error.error,
							title: 'Erro ao confirmar reserva',
							btnMessage: 'Fechar',
						},
					})
				},
			})
	}

	private initializeFieldState(fieldName: string) {
		const focused = signal(false)
		const editing = signal(false)

		this.fieldStates.set(fieldName, {
			focused,
			editing,
			setFocused: (value: boolean) => focused.set(value),
			setEditing: (value: boolean) => editing.set(value),
		})
	}

	getFieldState(fieldName: string): FieldState {
		return this.fieldStates.get(fieldName)!
	}

	ngOnInit(): void {
		this.dataLayerService.logEvent({
			event: 'screen_view',
			screen_name: 'Gastronomia Porto Bank - Reserva',
			venueId: this.venueId,
		})
		this.store.dispatch(ReservationActions.resetReservationState())

		// Regras de habilitação simples e reativas
		this.form.get('date')!.valueChanges.subscribe(date => {
			if (date) this.form.controls.section.enable()
			else this.form.controls.section.disable()
		})

		this.form.get('section')!.valueChanges.subscribe(section => {
			if (section) this.form.controls.partySize.enable()
			else this.form.controls.partySize.disable()
		})

		this.form.get('partySize')!.valueChanges.subscribe(size => {
			if (size) this.form.controls.reservationTime.enable()
			else this.form.controls.reservationTime.disable()
		})

		// Inicial: todos desabilitados menos a data
		this.form.controls.section.disable({ emitEvent: false })
		this.form.controls.partySize.disable({ emitEvent: false })
		this.form.controls.reservationTime.disable({ emitEvent: false })

		this.venueId = this.route.snapshot.paramMap.get('venueId')
		if (this.venueId) {
			this.store.dispatch(VenueActions.getOneVenue({ venueId: this.venueId }))
			this.store.dispatch(ReservationActions.loadAvailabilities({ venueId: this.venueId }))
		}
		this.store
			.select(getMinMaxPartySize)
			.pipe(take(1))
			.subscribe(minMax => {
				const min = minMax?.min ?? 1
				this.partysize.set(min)
				this.setPartysize(min)
			})

		this.vm$.pipe(take(1)).subscribe(vm => {
			const fullName = vm.client?.fullName ?? ''
			this.form.patchValue({ name: fullName })
			this.form.get('name')?.setValue(fullName)
		})
	}

	onDaySelected(day: string) {
		this.setReservationDay(day)
		this.showCalendar.set(false)
		this.form.patchValue({ date: day })

		// força localtime
		const date = new Date(`${day}T00:00:00`)

		this.displayDate = date.toLocaleDateString('pt-BR', {
			day: '2-digit',
			month: '2-digit',
			year: 'numeric',
		})
	}

	onSectionChange(sectionLabel: string) {
		this.setSection(sectionLabel, new Event('change'))
		this.form.patchValue({ section: sectionLabel })
	}

	setReservationDay(day: string) {
		this.store.dispatch(ReservationActions.resetInitialStateSuccess())
		this.store.dispatch(ReservationActions.setSelectedDate({ selectedDate: day }))
	}

	setSection(sectionLabel: string, event: Event) {
		event.preventDefault()
		this.store.dispatch(ReservationActions.setSelectedSection({ selectedSectionLabel: sectionLabel }))
	}

	setPartysize(partysize: number) {
		this.store.dispatch(
			ReservationActions.setScheduleFromPartysize({ selectedPartySize: String(partysize) })
		)
	}

	setReservationTime(reservationTime: string) {
		this.store.dispatch(
			ReservationActions.setReservationTime({ selectedReservationTime: reservationTime })
		)
	}

	public toggleTermsAndConditions(value: boolean) {
		this.eula.set(value)
	}

	confirmReservation() {
		this.store.dispatch(ReservationActions.confirmReservation())

		// só vou atualizar o cliente se o eula from client for false e o eula for true
		if (!this.eulaFromClient() && this.eula()) {
			this.store.dispatch(
				ClientActions.updateClientBackend({ client: { agreements: { eula: this.eula() } } })
			)
		}
	}

	isReservationDisabled(vm: ExtractObservableType<typeof this.vm$>) {
		return (
			!vm.selectedSection ||
			!vm.selectedPartySize ||
			!vm.selectedDate ||
			!vm.selectedReservationTime ||
			!this.eula()
		)
	}

	okClick() {
		this.store.dispatch(ReservationActions.resetReservationState())
		this.router.navigate(['/'])
	}
}
